import { useEffect, useRef } from 'react';

/** How often a visible page refreshes itself. */
export const AUTO_REFRESH_INTERVAL_MS = 60_000;

/** Calls closer together than this collapse into one (a tab switch fires both focus and visibilitychange). */
const MIN_GAP_MS = 2_000;

/**
 * Keep a page current while someone is working from it: re-run `refresh` when
 * the tab or window regains focus, and every `intervalMs` while the tab is
 * visible. Nothing runs while the tab is hidden.
 *
 * Built for the Notifications page (28 Sep 2026): a buyer working through a
 * day's date-change notifications came back to a stale list and had to reopen
 * it from the bell every time.
 *
 * @param refresh - the quiet reload to run; the latest function passed is always the one called
 * @param intervalMs - period while visible (default {@link AUTO_REFRESH_INTERVAL_MS})
 * @returns nothing
 * @example
 * useAutoRefresh(() => fetchNotifications(true));
 */
export function useAutoRefresh(refresh: () => void, intervalMs: number = AUTO_REFRESH_INTERVAL_MS): void {
  const latest = useRef(refresh);
  latest.current = refresh;

  useEffect(() => {
    let lastRun = Date.now();
    const run = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRun < MIN_GAP_MS) return;
      lastRun = now;
      latest.current();
    };
    const timer = window.setInterval(run, intervalMs);
    document.addEventListener('visibilitychange', run);
    window.addEventListener('focus', run);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', run);
      window.removeEventListener('focus', run);
    };
  }, [intervalMs]);
}
